import React, { useEffect, useMemo, useState, useCallback } from "react";
import { parseISO } from "date-fns";
import { mdy, mdyDow, md } from "@/lib/dateFmt";
import Sidebar from "@/components/Sidebar";
import PeriodSelector from "@/components/PeriodSelector";
import ScheduleGrid from "@/components/ScheduleGrid";
import ShiftDialog from "@/components/ShiftDialog";
import EmployeeSheet from "@/components/EmployeeSheet";
import MasterScheduleEditor from "@/components/MasterScheduleEditor";
import NotesPanel from "@/components/NotesPanel";
import AuditLogPanel from "@/components/AuditLogPanel";
import CoverageDialog from "@/components/CoverageDialog";
import OTRulesPage from "@/pages/OTRulesPage";
import AttendanceTrackerPage from "@/pages/AttendanceTrackerPage";
import SchedulePostingPage from "@/pages/SchedulePostingPage";
import ScheduleLegend from "@/components/ScheduleLegend";
import TradeRequestDialog from "@/components/TradeRequestDialog";
import AvailabilityPage from "@/pages/AvailabilityPage";
import MyInfoPage from "@/pages/MyInfoPage";
import NotificationTemplatesPage from "@/pages/NotificationTemplatesPage";
import ShiftFarmPage from "@/pages/ShiftFarmPage";
import EmployeesPage from "@/pages/EmployeesPage";
import MeetingsPage from "@/pages/MeetingsPage";
import ShiftCodesPage from "@/pages/ShiftCodesPage";
import RequestsPage from "@/pages/RequestsPage";
import MyDashboardPage from "@/pages/MyDashboardPage";
import OnCallSchedulePage from "@/pages/OnCallSchedulePage";
import NsoPage from "@/pages/NsoPage";
import AutoAssignButton from "@/components/AutoAssignButton";
import PtoRequestDialog from "@/components/PtoRequestDialog";
import PickupRequestDialog from "@/components/PickupRequestDialog";
import ResetAllDialog from "@/components/ResetAllDialog";
import ResetEmployeeDialog from "@/components/ResetEmployeeDialog";
import DayTimelineDialog from "@/components/DayTimelineDialog";
import OpenShiftManagerDialog from "@/components/OpenShiftManagerDialog";
import NotificationBell from "@/components/NotificationBell";
import FacilityContextSwitcher from "@/components/FacilityContextSwitcher";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFacility } from "@/context/FacilityContext";
import { Send, Users2, CalendarClock, Menu, RefreshCw, AlertTriangle, Printer, FileText, Sparkles, Loader2 } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast, Toaster } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const { facilityId, facilities } = useFacility();
  const isManager = user?.role === "manager" || user?.role === "admin";
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState(user?.role === "employee" ? "my-dashboard" : "schedule");

  // Sync tab with URL ?tab= so notification links like `/?tab=my-requests` or
  // `/?tab=requests` land on the right screen. Also allows sharing links.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    if (t && t !== tab) setTab(t);
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    const onPop = () => {
      const params = new URLSearchParams(window.location.search);
      const t = params.get("tab");
      if (t) setTab(t);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const handleTabChange = (nxt) => {
    setTab(nxt);
    const url = new URL(window.location.href);
    if (nxt) url.searchParams.set("tab", nxt); else url.searchParams.delete("tab");
    window.history.replaceState({}, "", url.toString());
  };

  // Mirror the current tab onto <body data-tab=...> so the landscape-immersive
  // CSS (index.css) can address the whole viewport, not just the app root.
  useEffect(() => {
    document.body.dataset.tab = tab;
    return () => { delete document.body.dataset.tab; };
  }, [tab]);
  const [periods, setPeriods] = useState([]);
  const [selectedPP, setSelectedPP] = useState(null);
  const [cadence, setCadence] = useState("biweekly");
  const [employees, setEmployees] = useState([]);
  const [schedule, setSchedule] = useState({ assignments: [], start: null, end: null, published: false });
  const [openShifts, setOpenShifts] = useState({ days: [] });
  const [codes, setCodes] = useState([]);
  const [dialog, setDialog] = useState({ open: false, employee: null, date: null, current: null });
  const [sheet, setSheet] = useState({ open: false, employee: null });
  const [ptoOpen, setPtoOpen] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [deptFilter, setDeptFilter] = useState("all");
  const [departments, setDepartments] = useState([]);
  const [roster, setRoster] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [coverageDialog, setCoverageDialog] = useState({ open: false, coverage: null, date: null });
  const [tradeOpen, setTradeOpen] = useState(false);
  const [pickupDialog, setPickupDialog] = useState({ open: false, date: null, code: null, codeMeta: null });
  const [openShiftMgr, setOpenShiftMgr] = useState({ open: false, date: null });
  const [resetAllOpen, setResetAllOpen] = useState(false);
  const [timelineIso, setTimelineIso] = useState(null);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [pendingOffers, setPendingOffers]     = useState(0);
  const [pendingByKind, setPendingByKind] = useState({});

  const loadRoster = useCallback(async () => {
    if (!facilityId) return;
    try { const { data } = await api.get(`/roster?facility_id=${facilityId}`); setRoster(data); } catch { /* ignore */ }
  }, [facilityId]);
  useEffect(() => { loadRoster(); }, [loadRoster]);
  // Only surface departments that are actually used at THIS facility (derived
  // from the facility's shift codes). Prevents the dropdown from listing every
  // global department when a facility only uses a few.
  useEffect(() => {
    if (!facilityId) { setDepartments([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/meta/shift-code-departments?facility_id=${facilityId}`);
        setDepartments((data || []).map(d => d.name).filter(Boolean));
      } catch { setDepartments([]); }
      // Reset filter if the previous choice isn't in this facility's list
      setDeptFilter("all");
    })();
  }, [facilityId]);

  const loadEmployees = useCallback(async () => {
    if (!facilityId) return;
    try { const { data } = await api.get(`/employees?facility_id=${facilityId}`); setEmployees(data); } catch { /* ignore */ }
  }, [facilityId]);
  const loadCodes = useCallback(async () => {
    if (!facilityId) return;
    try { const { data } = await api.get(`/meta/shift-codes?facility_id=${facilityId}`); setCodes(data); } catch { /* ignore */ }
  }, [facilityId]);
  const loadMyRequests = useCallback(async () => {
    try { const { data } = await api.get(`/pto-requests`); setMyRequests(data); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!facilityId) return;
    (async () => {
      try {
        const [meta, pp, emp, cs] = await Promise.all([
          api.get(`/facility-meta?facility_id=${facilityId}`),
          api.get(`/months?facility_id=${facilityId}`),
          api.get(`/employees?facility_id=${facilityId}`),
          api.get(`/meta/shift-codes?facility_id=${facilityId}`),
        ]);
        setCadence(meta.data?.cadence || "biweekly");
        setPeriods(pp.data); setEmployees(emp.data); setCodes(cs.data);
        const current = pp.data.find(p => p.is_current) || pp.data[0];
        setSelectedPP(current?.id);
      } catch { toast.error("Failed to load data"); }
    })();
  }, [facilityId]);

  useEffect(() => { if (user?.role === "employee") loadMyRequests(); }, [user, loadMyRequests]);

  const loadPendingCount = useCallback(async () => {
    if (!isManager || !facilityId) { setPendingRequests(0); setPendingByKind({}); return; }
    try {
      const [pto, trades, pickups, mods, avail, callouts] = await Promise.all([
        api.get(`/pto-requests?status=pending&facility_id=${facilityId}`),
        api.get(`/trade-requests?status=pending&facility_id=${facilityId}`),
        api.get(`/shift-pickup-requests?status=pending&facility_id=${facilityId}`),
        api.get(`/shift-modification-requests?status=pending&facility_id=${facilityId}`),
        api.get(`/availability-edit-requests?status=pending&facility_id=${facilityId}`).catch(() => ({ data: [] })),
        api.get(`/callout-requests?status=pending&facility_id=${facilityId}`).catch(() => ({ data: [] })),
      ]);
      const counts = {
        pto:      Array.isArray(pto.data)      ? pto.data.length      : 0,
        trades:   Array.isArray(trades.data)   ? trades.data.length   : 0,
        pickups:  Array.isArray(pickups.data)  ? pickups.data.length  : 0,
        mods:     Array.isArray(mods.data)     ? mods.data.length     : 0,
        avail:    Array.isArray(avail.data)    ? avail.data.length    : 0,
        callouts: Array.isArray(callouts.data) ? callouts.data.length : 0,
      };
      const total = Object.values(counts).reduce((a, b) => a + b, 0);
      setPendingByKind(counts);
      setPendingRequests(total);
    } catch { setPendingRequests(0); setPendingByKind({}); }
  }, [isManager, facilityId]);
  useEffect(() => { loadPendingCount(); }, [loadPendingCount, tab]);

  // Employee: pending shift-offer count → sidebar badge on "My Requests".
  const loadMyPendingOffers = useCallback(async () => {
    if (user?.role !== "employee") { setPendingOffers(0); return; }
    try {
      const { data } = await api.get("/my-shift-offers");
      const pending = (data || []).filter(o => o.my_status === "pending" && o.offer_status === "open");
      setPendingOffers(pending.length);
    } catch { setPendingOffers(0); }
  }, [user]);
  useEffect(() => { loadMyPendingOffers(); }, [loadMyPendingOffers, tab]);
  useEffect(() => {
    const t = setInterval(loadMyPendingOffers, 45_000);
    return () => clearInterval(t);
  }, [loadMyPendingOffers]);

  const [masterTemplate, setMasterTemplate] = useState(null);
  const [levelMap, setLevelMap] = useState({});
  const [adjacentPeek, setAdjacentPeek] = useState({ by_employee: {}, prev_date: null, next_date: null });
  const loadSchedule = useCallback(async (ppId) => {
    if (!ppId || !facilityId) return;
    const isMonthly = String(ppId).startsWith("mo-");
    try {
      const schedUrl   = isMonthly ? `/schedule-month/${ppId}`   : `/schedule/${ppId}`;
      const openUrl    = isMonthly ? `/schedule-month/${ppId}/open-shifts` : `/schedule/${ppId}/open-shifts`;
      const currentP = (periods || []).find(p => p.id === ppId);
      const anchorPP = isMonthly ? (currentP?.pay_periods?.[0]) : ppId;
      const mtUrl    = anchorPP ? `/master-template?facility_id=${facilityId}&pay_period_id=${anchorPP}` : `/master-template?facility_id=${facilityId}`;
      const peekUrl = !isMonthly ? `/schedule/${ppId}/adjacent-peek?facility_id=${facilityId}` : null;
      const [sched, os, mt, lvls, peek] = await Promise.all([
        api.get(`${schedUrl}?facility_id=${facilityId}`),
        api.get(`${openUrl}?facility_id=${facilityId}`),
        api.get(mtUrl),
        api.get(`/game/roster-levels?facility_id=${facilityId}`).catch(() => ({ data: {} })),
        peekUrl ? api.get(peekUrl).catch(() => ({ data: { by_employee: {} } })) : Promise.resolve({ data: { by_employee: {} } }),
      ]);
      setSchedule(sched.data);
      setOpenShifts(os.data);
      setMasterTemplate(mt.data);
      setLevelMap(lvls.data || {});
      setAdjacentPeek(peek.data || { by_employee: {} });
      loadPendingCount();
    } catch { toast.error("Failed to load schedule"); }
  }, [facilityId, loadPendingCount, periods]);

  useEffect(() => { loadSchedule(selectedPP); }, [selectedPP, loadSchedule]);

  // Poll pending offers every 15s so cells go from green → clear the moment
  // any recipient accepts (server side already flips log.status="filled").
  // We refetch schedule + open-shifts — cheap enough for a background poll.
  useEffect(() => {
    if (!selectedPP) return;
    const t = setInterval(() => loadSchedule(selectedPP), 15_000);
    return () => clearInterval(t);
  }, [selectedPP, loadSchedule]);

  // Auto-refresh schedule whenever the user navigates BACK to the Schedule tab
  // (e.g. after approving/denying requests on the Requests tab, the grid should
  // reflect changes immediately without a manual page refresh).
  useEffect(() => {
    if (tab === "schedule" && selectedPP && facilityId) {
      loadSchedule(selectedPP);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const currentPeriod = useMemo(() => {
    const found = periods.find(p => p.id === selectedPP);
    if (found) return found;
    if (!selectedPP) return null;
    try {
      if (String(selectedPP).startsWith("mo-")) {
        const [_, y, m] = selectedPP.match(/^mo-(\d{4})-(\d{2})$/) || [];
        if (y && m) {
          const dt = new Date(Number(y), Number(m) - 1, 1);
          return { id: selectedPP, label: dt.toLocaleDateString("en-US", { month: "long", year: "numeric" }), is_current: false, published: schedule.published };
        }
      }
      const [y, m, d] = selectedPP.replace("pp-", "").split("-").map(Number);
      const start = new Date(y, m - 1, d);
      const end = new Date(y, m - 1, d + 13);
      const fmt = (dt) => `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`;
      return { id: selectedPP, label: `${fmt(start)} – ${fmt(end)}`, is_current: false, published: schedule.published };
    } catch { return null; }
  }, [selectedPP, periods, schedule.published]);

  const visibleEmployees = useMemo(() => {
    // Everyone in the facility (employees can see peers). Filter by department if set.
    // Employees viewing an unpublished period see ONLY their own row — peers stay hidden
    // until the manager publishes, matching what the backend serves for /schedule.
    let list = employees;
    if (!list) return [];
    if (!isManager && schedule && !schedule.published && user?.id) {
      list = list.filter((e) => e.id === user.id);
    }
    if (deptFilter === "all") return list;
    return list.filter((e) => (e.departments_trained || []).includes(deptFilter));
  }, [employees, deptFilter, isManager, schedule, user]);

  // Build interleaved roster: only separators from roster whose neighbors survive filtering
  const rosterView = useMemo(() => {
    if (!roster.length) return visibleEmployees.map((e) => ({ ...e, type: "employee" }));
    const visibleIds = new Set(visibleEmployees.map((e) => e.id));
    return roster.filter((it) => it.type === "separator" || visibleIds.has(it.id));
  }, [roster, visibleEmployees]);

  const openCell = (emp, date, current) => {
    if (!isManager) return;
    setDialog({ open: true, employee: emp, date, current });
  };

  const publish = async () => {
    try {
      const isMonthly = String(selectedPP).startsWith("mo-");
      if (isMonthly) {
        // Publish/unpublish every constituent pp of the month.
        const pps = currentPeriod?.pay_periods || [];
        for (const ppId of pps) {
          // eslint-disable-next-line no-await-in-loop
          await api.post("/schedule/publish", { pay_period_id: ppId, published: !schedule.published, facility_id: facilityId });
        }
      } else {
        await api.post("/schedule/publish", { pay_period_id: selectedPP, published: !schedule.published, facility_id: facilityId });
      }
      const pp = await api.get(`/months?facility_id=${facilityId}`);
      setPeriods(pp.data);
      await loadSchedule(selectedPP);
      toast.success(!schedule.published ? "Published" : "Unpublished");
    } catch { toast.error("Failed"); }
  };

  const [resetEmp, setResetEmp] = useState(null);
  const resetEmployeeToMaster = (emp) => {
    // Open the rich per-employee dialog instead of a blunt confirm — lets
    // the manager choose scope (this pp / future N periods) and pick which
    // codes to keep untouched (PTO, LOA, U, etc.).
    setResetEmp(emp);
  };

  return (
    <div className="min-h-screen flex bg-slate-50" data-tab={tab} data-testid="app-root">
      <Toaster position="top-right" richColors />
      <Sidebar active={tab} onChange={handleTabChange} open={sidebarOpen} onClose={() => setSidebarOpen(false)} badges={{ requests: pendingRequests, "my-requests": pendingOffers }} />
      <main className="flex-1 min-w-0 w-full">
        <div className="app-top-header sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200" style={{ paddingTop: "env(safe-area-inset-top)" }}>
          <div className="px-2 md:px-6 py-2 md:py-4 flex items-center justify-between gap-2 md:gap-3 flex-wrap md:flex-nowrap">
            <div className="flex items-center gap-2 md:gap-3 min-w-0">
              <button
                className="lg:hidden p-2 -ml-1 rounded-md hover:bg-slate-100 text-slate-700"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open menu"
                data-testid="btn-open-sidebar"
              >
                <Menu size={18} />
              </button>
              <FacilityContextSwitcher />
              <div className="min-w-0 hidden sm:block">
                <div className="text-[10px] md:text-xs uppercase tracking-widest text-slate-500">Pay Period</div>
                <div className="font-display text-sm md:text-lg font-semibold tracking-tight text-slate-900 truncate">
                  {currentPeriod?.label || "—"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 md:gap-3 flex-nowrap min-w-0">
              {(tab === "schedule" || tab === "master") && selectedPP && (
                <PeriodSelector
                  cadence={cadence}
                  periods={periods}
                  value={selectedPP}
                  onChange={setSelectedPP}
                  published={schedule.published}
                />
              )}
              {user?.role === "employee"
                && (!user?.primary_facility_id || user?.primary_facility_id === facilityId)
                && (
                <Button className="bg-fuchsia-600 hover:bg-fuchsia-700" onClick={() => setPtoOpen(true)} data-testid="btn-request-pto">
                  <CalendarClock size={14} className="md:mr-1" /> <span className="hidden md:inline">Request PTO</span>
                </Button>
              )}
              {user?.role === "admin" && tab === "schedule" && (
                <Button
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50 hidden md:inline-flex"
                  onClick={() => setResetAllOpen(true)}
                  data-testid="btn-refresh-all"
                >
                  <RefreshCw size={14} className="md:mr-1" /> <span className="hidden md:inline">Refresh all</span>
                </Button>
              )}
              {tab === "schedule" && selectedPP && facilityId && (
                <Button
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-50 hidden md:inline-flex"
                  onClick={() => {
                    const url = `/print/schedule?facility_id=${encodeURIComponent(facilityId)}&pp_id=${encodeURIComponent(selectedPP)}`;
                    window.open(url, "_blank", "noopener");
                  }}
                  data-testid="btn-print-schedule"
                >
                  <Printer size={14} className="md:mr-1" /> <span className="hidden md:inline">Print</span>
                </Button>
              )}
              {isAdmin && tab === "schedule" && selectedPP && facilityId && (
                <AutoAssignButton facilityId={facilityId} payPeriodId={selectedPP} onDone={() => loadSchedule(selectedPP)} />
              )}
              {isAdmin && tab === "schedule" && selectedPP && facilityId && (
                <Button
                  variant="outline"
                  className="border-indigo-400 text-indigo-700 hover:bg-indigo-50 hidden md:inline-flex"
                  onClick={async () => {
                    try {
                      const url = `${api.defaults.baseURL}/report/pay-period-pdf?facility_id=${encodeURIComponent(facilityId)}&pay_period_id=${encodeURIComponent(selectedPP)}`;
                      const tok = localStorage.getItem("token") || "";
                      const resp = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
                      if (!resp.ok) throw new Error(await resp.text());
                      const blob = await resp.blob();
                      const a = document.createElement("a");
                      a.href = URL.createObjectURL(blob);
                      a.download = `${facilityId}-${selectedPP}-report.pdf`;
                      a.click();
                      URL.revokeObjectURL(a.href);
                    } catch (e) { toast.error("Report failed"); }
                  }}
                  data-testid="btn-report"
                >
                  <FileText size={14} className="md:mr-1" /> <span className="hidden md:inline">Report</span>
                </Button>
              )}
              {isManager && tab === "schedule" && (
                <Button
                  className={`${schedule.published ? "bg-slate-200 text-slate-800 hover:bg-slate-300" : "bg-blue-600 hover:bg-blue-700"} px-2 md:px-3`}
                  onClick={publish}
                  data-testid="btn-publish"
                >
                  <Send size={14} className="md:mr-1" />
                  <span className="hidden sm:inline">{schedule.published ? "Unpublish" : "Publish"}</span>
                </Button>
              )}
              <NotificationBell />
            </div>
          </div>
        </div>

        {/* Small laptops: on the schedule tab, remove outer page scroll and
             pin the app header only until the user starts scrolling the grid.
             The grid then takes 100dvh - header for maximum use of the screen. */}
        <div className={`p-3 md:p-6 space-y-4 md:space-y-6 ${tab === "schedule" ? "sched-tab-wrap" : ""}`}>
          {tab === "schedule" && (
            <>
              {/* Mobile: tiny compact strip; Desktop: full 4-tile row. */}
              <div className="md:hidden flex items-center gap-3 text-[11px] font-semibold uppercase tracking-widest text-slate-500 tabular-nums px-1 -mb-1 sched-chrome" data-testid="sched-stats-mobile">
                <span><b className="text-slate-800">{visibleEmployees.length}</b> emps</span>
                <span className="text-slate-300">·</span>
                <span><b className="text-slate-800">{schedule.assignments.length}</b> shifts</span>
                <span className="text-slate-300">·</span>
                {(() => {
                  const openCount = openShifts.days.reduce((s, d) => s + (d.open?.length || 0), 0);
                  return <span className={openCount === 0 ? "text-emerald-700" : "text-amber-700"}><b>{openCount}</b> open</span>;
                })()}
                <span className="text-slate-300">·</span>
                <span className={schedule.published ? "text-emerald-700" : "text-slate-700"}>{schedule.published ? "Published" : "Draft"}</span>
              </div>
              <div className="hidden md:grid grid-cols-2 md:grid-cols-4 gap-3 sched-chrome">
                <Stat label="Employees" value={visibleEmployees.length} />
                <Stat label="Shifts assigned" value={schedule.assignments.length} />
                <Stat label="Open slots"
                  value={openShifts.days.reduce((s, d) => s + (d.open?.length || 0), 0)}
                  accent={openShifts.days.reduce((s, d) => s + (d.open?.length || 0), 0) === 0 ? "emerald" : "amber"}
                />
                <Stat label="Status" value={schedule.published ? "Published" : "Draft"} accent={schedule.published ? "emerald" : "slate"} />
              </div>

              {schedule.start && schedule.end ? (
                <>
                  <div className="flex items-center gap-2 flex-wrap sched-chrome">
                    <div className="text-xs uppercase tracking-widest text-slate-500 hidden sm:block">Filter by department:</div>
                    <Select value={deptFilter} onValueChange={setDeptFilter}>
                      <SelectTrigger className="w-full max-w-[220px] h-8" data-testid="dept-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All departments</SelectItem>
                        {departments.map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    {deptFilter !== "all" && (
                      <div className="text-xs text-slate-500">
                        Showing {visibleEmployees.length} of {employees.length}
                      </div>
                    )}
                  </div>
                  {isManager && <div data-testid="open-shifts-strip-placeholder" className="hidden" />}
                  {schedule && !schedule.published && (
                    <div className="rounded-2xl border-2 border-amber-400 bg-gradient-to-r from-amber-50 via-amber-100 to-amber-50 p-4 flex items-start gap-3 shadow-sm" data-testid="unpublished-banner">
                      <div className="w-10 h-10 rounded-xl bg-amber-500 text-white grid place-items-center shrink-0 shadow">
                        <AlertTriangle className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-black text-amber-900 uppercase tracking-widest text-sm">
                          {isManager ? "Not Published — Pending" : "Pending — Not Yet Published"}
                        </div>
                        <div className="text-xs text-amber-800 mt-0.5">
                          {isManager
                            ? "Employees can't see this schedule yet. Publish it from the \"Draft / Publish\" toggle above the grid when it's ready."
                            : "This is a preview of your own shifts only. PTO approvals, trades, or shift changes may still shift things around before your manager publishes the final schedule."}
                        </div>
                      </div>
                    </div>
                  )}
                  <ScheduleLegend />
                  <ScheduleGrid
                    start={schedule.start} end={schedule.end}
                    employees={visibleEmployees}
                    roster={rosterView}
                    assignments={schedule.assignments}
                    coverageShifts={schedule.coverage_shifts || []}
                    availabilityByEmployee={schedule.availability_by_employee || {}}
                    pendingOffers={schedule.pending_offers || []}
                    standaloneNotesByEmployee={schedule.standalone_notes_by_employee || {}}
                    nsoByEmployee={schedule.nso_by_employee || {}}
                    adjacentPeek={adjacentPeek}
                    codes={codes}
                    canEdit={isManager}
                    levelMap={levelMap}
                    dividers={schedule.pp_dividers || []}
                    onCellClick={openCell}
                    onEmployeeClick={(emp) => setSheet({ open: true, employee: emp })}
                    onResetEmployee={isManager ? resetEmployeeToMaster : null}
                    openDays={(user?.role === "employee" && schedule && !schedule.published) ? [] : openShifts.days}
                    onCoverageClick={(cov, date) => setCoverageDialog({ open: true, coverage: cov, date })}
                    onCoverageCellClick={(covShift) => setCoverageDialog({ open: true, coverage: covShift, date: covShift.date })}
                    onOpenChipClick={user?.role === "employee" ? (iso, code, meta) => setPickupDialog({ open: true, date: iso, code, codeMeta: meta }) : (iso) => setOpenShiftMgr({ open: true, date: iso })}
                    onDayHeaderClick={(iso) => setTimelineIso(iso)}
                    onCellReveal={(emp, iso, a) => {
                      if (!a) return;
                      const dateStr = mdyDow(iso);
                      const timeLine = a.start_time && a.end_time ? `${a.start_time}–${a.end_time}` : null;
                      if (a.external) {
                        toast.message(`${emp.name} · ${dateStr}`, {
                          description: `Working at ${a.external_facility_label} · ${a.shift_code} · ${a.hours || 0}h${timeLine ? ` (${timeLine})` : ""}`,
                        });
                      } else {
                        toast.message(`${emp.name} · ${dateStr}`, {
                          description: `${a.shift_code}${a.is_modified ? " (modified)" : ""} · ${a.hours || 0}h${timeLine ? ` · ${timeLine}` : ""}${a.covered_by_employee_name ? ` · Covered by ${a.covered_by_employee_name}` : ""}`,
                        });
                      }
                    }}
                  />
                </>
              ) : (
                !isManager && (
                  <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center" data-testid="unpublished-empty">
                    <Users2 className="mx-auto text-slate-300" />
                    <div className="mt-2 font-medium">This pay period isn&apos;t published yet.</div>
                    <div className="text-sm text-slate-500">Your manager will publish it soon.</div>
                  </div>
                )
              )}
            </>
          )}

          {tab === "requests" && isManager && <RequestsPage pendingCounts={pendingByKind} onChange={loadPendingCount} />}
          {tab === "my-requests" && user?.role === "employee" && (
            <MyRequests
              items={myRequests}
              onOpenPto={() => setPtoOpen(true)}
              onOpenTrade={() => setTradeOpen(true)}
              onCancelled={() => { loadMyRequests(); loadSchedule(selectedPP); loadPendingCount(); }}
            />
          )}
          {tab === "employees" && isManager && (
            <EmployeesPage employees={employees} onChange={loadEmployees} facilityId={facilityId} />
          )}
          {tab === "meetings" && user?.role === "admin" && (
            <MeetingsPage />
          )}
          {tab === "shift-codes" && isManager && (
            <ShiftCodesPage codes={codes} onChange={loadCodes} facilityId={facilityId} />
          )}
          {tab === "master" && isManager && (
            <MasterScheduleEditor codes={codes} payPeriodId={selectedPP} payPeriodLabel={currentPeriod?.label} facilityId={facilityId} />
          )}
          {tab === "ot-rules" && isManager && (
            <OTRulesPage facilityId={facilityId} facilityLabel={facilities.find(f => f.id === facilityId)?.label} />
          )}
          {tab === "attendance" && isManager && (
            <AttendanceTrackerPage facilityId={facilityId} facilityLabel={facilities.find(f => f.id === facilityId)?.label} />
          )}
          {tab === "sched-posting" && user?.role === "admin" && (
            <SchedulePostingPage facilityId={facilityId} facilityLabel={facilities.find(f => f.id === facilityId)?.label} />
          )}
          {tab === "templates" && user?.role === "admin" && (
            <NotificationTemplatesPage facilityId={facilityId} facilityLabel={facilities.find(f => f.id === facilityId)?.label} />
          )}
          {tab === "shift-farm" && (
            <ShiftFarmPage facilityId={facilityId} facilityLabel={facilities.find(f => f.id === facilityId)?.label} />
          )}
          {tab === "availability" && (
            <AvailabilityPage facilityId={facilityId} />
          )}
          {tab === "notes" && (
            <NotesPanel payPeriodId={selectedPP} canEdit={isManager} employees={employees} facilityId={facilityId} />
          )}
          {tab === "audit" && isManager && <AuditLogPanel />}
          {tab === "my-info" && <MyInfoPage />}
          {tab === "my-dashboard" && (
            <MyDashboardPage facilityId={facilityId} facilityLabel={facilities.find(f => f.id === facilityId)?.label} />
          )}
          {tab === "oncall" && isManager && (
            <OnCallSchedulePage facilityId={facilityId} facilityLabel={facilities.find(f => f.id === facilityId)?.label} />
          )}
          {tab === "nso" && (
            <NsoPage facilityId={facilityId} />
          )}
        </div>
      </main>

      <ShiftDialog
        open={dialog.open}
        onClose={() => setDialog({ ...dialog, open: false })}
        employee={dialog.employee} date={dialog.date} current={dialog.current} codes={codes}
        facilityId={facilityId}
        dayAssignments={(schedule?.assignments || []).filter((a) => a.date === dialog.date)}
        onSaved={() => loadSchedule(selectedPP)}
      />
      <EmployeeSheet
        open={sheet.open}
        onClose={() => setSheet({ open: false, employee: null })}
        employee={sheet.employee} assignments={schedule.assignments} codes={codes}
        canEdit={isManager} payPeriodId={selectedPP}
        onSchedulingChange={() => { loadSchedule(selectedPP); loadEmployees(); }}
      />
      <PtoRequestDialog
        open={ptoOpen}
        onClose={() => setPtoOpen(false)}
        facilityId={facilityId}
        onSubmitted={() => { loadSchedule(selectedPP); loadMyRequests(); }}      />
      <CoverageDialog
        open={coverageDialog.open}
        onClose={() => setCoverageDialog({ open: false, coverage: null, date: null })}
        coverage={coverageDialog.coverage}
        date={coverageDialog.date}
        facilityId={facilityId}
        onSaved={() => loadSchedule(selectedPP)}
      />
      <TradeRequestDialog
        open={tradeOpen}
        onClose={() => setTradeOpen(false)}
        facilityId={facilityId}
        currentUserId={user?.id}
        onSubmitted={() => { loadMyRequests(); loadPendingCount(); }}
      />
      <PickupRequestDialog
        open={pickupDialog.open}
        onClose={() => setPickupDialog({ open: false, date: null, code: null, codeMeta: null })}
        facilityId={facilityId}
        facilityLabel={facilities.find(f => f.id === facilityId)?.label}
        date={pickupDialog.date}
        code={pickupDialog.code}
        codeMeta={pickupDialog.codeMeta}
        onSubmitted={() => { loadMyRequests(); loadPendingCount(); }}
      />
      <ResetAllDialog
        open={resetAllOpen}
        onClose={() => setResetAllOpen(false)}
        facilityId={facilityId}
        facilityLabel={facilities.find(f => f.id === facilityId)?.label}
        payPeriodId={selectedPP}
        payPeriodLabel={currentPeriod?.label}
        cadence={cadence}
        codes={codes}
        onDone={() => loadSchedule(selectedPP)}
      />
      <ResetEmployeeDialog
        open={!!resetEmp}
        onClose={() => setResetEmp(null)}
        employee={resetEmp}
        payPeriodId={selectedPP}
        payPeriodLabel={currentPeriod?.label}
        codes={codes}
        onDone={() => loadSchedule(selectedPP)}
      />
      <OpenShiftManagerDialog
        open={openShiftMgr.open}
        onClose={() => setOpenShiftMgr({ open: false, date: null })}
        facilityId={facilityId}
        facilityLabel={facilities.find(f => f.id === facilityId)?.label}
        dateIso={openShiftMgr.date}
        codes={codes}
        currentUserName={user?.name}
        onMutated={() => loadSchedule(selectedPP)}
      />

      <DayTimelineDialog
        open={!!timelineIso}
        onClose={() => setTimelineIso(null)}
        iso={timelineIso}
        employees={schedule.employees || []}
        roster={schedule.roster || []}
        assignments={schedule.assignments || []}
        codes={codes}
        canEdit={isManager}
        masterTemplate={masterTemplate}
        payPeriodStart={schedule.start}
        onSaved={() => loadSchedule(selectedPP)}
      />
    </div>
  );
}

function Stat({ label, value, accent = "slate" }) {
  const map = { slate: "text-slate-800", emerald: "text-emerald-700", amber: "text-amber-700" };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 font-display text-2xl font-semibold tracking-tight tabular-nums ${map[accent]}`}>{value}</div>
    </div>
  );
}

function MyRequests({ items, onOpenPto, onOpenTrade, onCancelled }) {
  const { user } = useAuth();
  const { facilityId } = useFacility();
  // Employees on a secondary facility can only view + pick up open shifts;
  // the "New Trade" / "New PTO" buttons are hidden here so they don't dead-end
  // on the backend `require_primary_facility` block.
  const atSecondary = user?.role === "employee"
    && !!user?.primary_facility_id
    && !!facilityId
    && facilityId !== user.primary_facility_id;
  const [tradeItems, setTradeItems] = useState([]);
  const [pickupItems, setPickupItems] = useState([]);
  const [offers, setOffers]         = useState([]);
  const [respondingId, setRespondingId] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const reloadOffers = useCallback(async () => {
    try { const { data } = await api.get("/my-shift-offers"); setOffers(data || []); }
    catch { setOffers([]); }
  }, []);
  const reloadTrades = useCallback(async () => {
    try { const { data } = await api.get("/trade-requests/mine"); setTradeItems(data || []); }
    catch { setTradeItems([]); }
  }, []);
  const reloadPickups = useCallback(async () => {
    try { const { data } = await api.get("/shift-pickup-requests/mine"); setPickupItems(data || []); }
    catch { setPickupItems([]); }
  }, []);
  useEffect(() => {
    reloadTrades();
    reloadPickups();
    reloadOffers();
  }, [items, reloadOffers, reloadTrades, reloadPickups]);

  const cancelPto = async (r, singleDate = null) => {
    const label = singleDate
      ? `Cancel PTO for ${singleDate}?`
      : (r.status === "approved"
          ? `Cancel this approved PTO? Your prior schedule for those dates will be restored.`
          : `Cancel this PTO request?`);
    if (!window.confirm(label)) return;
    setCancelling(`pto:${r.id}:${singleDate || "all"}`);
    try {
      if (singleDate) await api.delete(`/pto-requests/${r.id}/date/${singleDate}`);
      else            await api.delete(`/pto-requests/${r.id}`);
      toast.success("PTO cancelled");
      onCancelled?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setCancelling(null); }
  };
  const cancelTrade = async (r) => {
    const label = r.status === "approved"
      ? "Cancel this approved trade? The two shifts will be swapped back to their original owners."
      : "Cancel this trade request?";
    if (!window.confirm(label)) return;
    setCancelling(`trade:${r.id}`);
    try {
      await api.delete(`/trade-requests/${r.id}`);
      toast.success("Trade cancelled");
      reloadTrades();
      onCancelled?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setCancelling(null); }
  };
  const cancelPickup = async (r) => {
    const label = r.status === "approved"
      ? "Cancel this approved pickup? The shift will be removed from your schedule and re-opened."
      : "Cancel this pickup request?";
    if (!window.confirm(label)) return;
    setCancelling(`pickup:${r.id}`);
    try {
      await api.delete(`/shift-pickup-requests/${r.id}`);
      toast.success("Pickup cancelled");
      reloadPickups();
      onCancelled?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setCancelling(null); }
  };

  // Auto-open "Shift offers" section when the notification link `?sub=offers` is present.
  const initialSub = React.useMemo(() => {
    try { return new URLSearchParams(window.location.search).get("sub"); } catch { return null; }
  }, []);
  const offersRef = React.useRef(null);
  useEffect(() => {
    if (initialSub === "offers" && offersRef.current) {
      offersRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [initialSub, offers.length]);

  const respond = async (offer, action) => {
    setRespondingId(offer.offer_id);
    try {
      const { data } = await api.post(`/my-shift-offers/${offer.offer_id}/respond`, { action });
      if (data.status === "accepted") toast.success("Shift accepted! It's on your schedule now.");
      else if (data.status === "denied") toast.success("Response recorded.");
      else if (data.status === "already_filled") toast("Someone else grabbed it first.", { duration: 5000 });
      await reloadOffers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't record response");
    }
    setRespondingId(null);
  };

  const pendingOffers = offers.filter(o => o.my_status === "pending" && o.offer_status === "open");
  const pastOffers    = offers.filter(o => !(o.my_status === "pending" && o.offer_status === "open"));
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="my-requests">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-lg font-display font-semibold">My Requests</div>
          <div className="text-sm text-slate-500">Track PTO and shift trades you&apos;ve submitted.</div>
        </div>
        <div className="flex gap-2">
          {!atSecondary && (
            <>
              <button onClick={onOpenTrade} className="text-sm rounded-md bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5" data-testid="new-trade-btn">
                + Trade
              </button>
              <button onClick={onOpenPto} className="text-sm rounded-md bg-fuchsia-600 hover:bg-fuchsia-700 text-white px-3 py-1.5" data-testid="new-pto-btn">
                + PTO
              </button>
            </>
          )}
        </div>
      </div>
      <div className="space-y-4">
        {/* Shift offers — surfaced first so employees never miss them */}
        <div ref={offersRef} data-testid="my-shift-offers-section">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
            Shift offers
            {pendingOffers.length > 0 && (
              <span className="rounded-full bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 tabular-nums">{pendingOffers.length} pending</span>
            )}
          </div>
          {pendingOffers.length === 0 && pastOffers.length === 0 && (
            <div className="text-sm text-slate-500">No open shifts offered to you yet.</div>
          )}
          <div className="space-y-2">
            {pendingOffers.map((o) => (
              <div key={o.offer_id} className="border-2 border-amber-300 bg-amber-50 rounded-xl p-3" data-testid={`offer-${o.offer_id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-sm min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">
                      {o.shift_label} <span className="text-slate-500 font-normal">({o.shift_code})</span>
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      {mdyDow(o.date)}
                      {o.shift_start && o.shift_end && <> · {o.shift_start}–{o.shift_end}</>}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Offered by {o.sent_by_name || "your manager"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => respond(o, "deny")}
                      disabled={respondingId === o.offer_id}
                      data-testid={`offer-deny-${o.offer_id}`}
                      className="text-sm rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 font-semibold"
                    >Decline</button>
                    <button
                      onClick={() => respond(o, "accept")}
                      disabled={respondingId === o.offer_id}
                      data-testid={`offer-accept-${o.offer_id}`}
                      className="text-sm rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-3 py-1.5 font-semibold"
                    >Accept</button>
                  </div>
                </div>
              </div>
            ))}
            {pastOffers.slice(0, 5).map((o) => (
              <div key={o.offer_id} className="border border-slate-200 rounded-xl p-3 opacity-80" data-testid={`past-offer-${o.offer_id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm min-w-0 flex-1">
                    <div className="font-medium text-slate-800 truncate">
                      {o.shift_label} <span className="text-slate-500 font-normal">({o.shift_code})</span>
                      <span className="text-xs text-slate-500 ml-2">
                        {mdy(o.date)}
                      </span>
                    </div>
                  </div>
                  <span className={`text-[11px] uppercase tracking-widest font-semibold rounded-md border px-2 py-0.5 ${
                    o.my_status === "accepted" ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
                    o.my_status === "denied"   ? "bg-slate-100 text-slate-600 border-slate-300" :
                    o.my_status === "too_late" ? "bg-amber-100 text-amber-800 border-amber-300" :
                    o.offer_status === "filled"? "bg-slate-100 text-slate-500 border-slate-300 italic" :
                                                 "bg-slate-100 text-slate-500 border-slate-300"
                  }`}>
                    {o.my_status === "too_late"       ? "Too late"
                    : o.my_status === "accepted"     ? "Accepted"
                    : o.my_status === "denied"       ? "Declined"
                    : o.offer_status === "filled"    ? `Filled by ${o.filled_by_name || "someone else"}`
                                                     : o.my_status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">PTO</div>
          <div className="space-y-2">
            {items.length === 0 && <div className="text-sm text-slate-500">No PTO requests yet.</div>}
            {items.map((r) => {
              const isCancelled = r.status === "cancelled";
              const canCancel = !isCancelled && r.status !== "denied";
              const dateDecisions = r.date_decisions || {};
              const multi = (r.dates || []).length > 1;
              return (
              <div key={r.id} className={`border rounded-xl p-3 ${isCancelled ? "border-slate-200 opacity-60" : "border-slate-200"}`} data-testid={`my-pto-${r.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm">
                    <div className="font-medium">{r.dates.map((d) => mdy(d)).join(", ")}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{r.pto_hours || 0}h PTO · {r.work_days || 0} work day(s)</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] uppercase tracking-widest font-semibold rounded-md border px-2 py-0.5 ${
                      r.status === "approved"  ? "bg-sky-100 text-sky-800 border-sky-300" :
                      r.status === "denied"    ? "bg-red-100 text-red-700 border-red-300" :
                      r.status === "cancelled" ? "bg-slate-100 text-slate-600 border-slate-300" :
                                                 "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300"
                    }`}>{r.status}</span>
                    {canCancel && (
                      <button
                        onClick={() => cancelPto(r)}
                        disabled={cancelling === `pto:${r.id}:all`}
                        className="text-[11px] rounded-md border border-red-300 bg-red-50 hover:bg-red-100 text-red-700 px-2 py-0.5 font-semibold disabled:opacity-50"
                        data-testid={`cancel-pto-${r.id}`}
                      >Cancel</button>
                    )}
                  </div>
                </div>
                {r.manager_note && <div className="mt-2 text-xs italic text-slate-600">Manager note: {r.manager_note}</div>}
                {/* Per-date cancel breakdown when multi-day AND still active */}
                {canCancel && multi && (
                  <div className="mt-2 pt-2 border-t border-slate-100 space-y-0.5">
                    {(r.dates || []).map(d => {
                      const dd = dateDecisions[d];
                      const isDateCancelled = dd?.status === "cancelled" || dd?.status === "denied";
                      return (
                        <div key={d} className="flex items-center justify-between text-[11px]">
                          <span className={isDateCancelled ? "line-through text-slate-400" : "text-slate-700"}>
                            {mdyDow(d)}
                            {dd && dd.status === "approved" && <span className="ml-1 text-sky-600 font-semibold">approved</span>}
                            {dd && dd.status === "denied"   && <span className="ml-1 text-red-600 font-semibold">denied</span>}
                            {dd && dd.status === "cancelled"&& <span className="ml-1 text-slate-500 font-semibold">cancelled</span>}
                          </span>
                          {!isDateCancelled && (
                            <button
                              onClick={() => cancelPto(r, d)}
                              disabled={cancelling === `pto:${r.id}:${d}`}
                              className="text-[10px] text-red-600 hover:underline disabled:opacity-50"
                              data-testid={`cancel-pto-date-${r.id}-${d}`}
                            >Cancel this date</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );})}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Trades</div>
          <div className="space-y-2">
            {tradeItems.length === 0 && <div className="text-sm text-slate-500">No trade requests yet.</div>}
            {tradeItems.map((r) => {
              const isCancelled = r.status === "cancelled";
              const canCancel = !isCancelled && r.status !== "denied";
              return (
              <div key={r.id} className={`border rounded-xl p-3 ${isCancelled ? "border-slate-200 opacity-60" : "border-slate-200"}`} data-testid={`my-trade-${r.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm">
                    <div className="font-medium">
                      {r.requester_name} → {r.partner_name}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {mdy(r.requester_date)} ({r.requester_shift_code})
                      &nbsp;↔&nbsp;
                      {mdy(r.partner_date)} ({r.partner_shift_code})
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] uppercase tracking-widest font-semibold rounded-md border px-2 py-0.5 ${
                      r.status === "approved"  ? "bg-sky-100 text-sky-800 border-sky-300" :
                      r.status === "denied"    ? "bg-red-100 text-red-700 border-red-300" :
                      r.status === "cancelled" ? "bg-slate-100 text-slate-600 border-slate-300" :
                                                 "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300"
                    }`}>{r.status}</span>
                    {canCancel && (
                      <button
                        onClick={() => cancelTrade(r)}
                        disabled={cancelling === `trade:${r.id}`}
                        className="text-[11px] rounded-md border border-red-300 bg-red-50 hover:bg-red-100 text-red-700 px-2 py-0.5 font-semibold disabled:opacity-50"
                        data-testid={`cancel-trade-${r.id}`}
                      >Cancel</button>
                    )}
                  </div>
                </div>
                {r.manager_note && <div className="mt-2 text-xs italic text-slate-600">Manager note: {r.manager_note}</div>}
              </div>
            );})}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Pickups</div>
          <div className="space-y-2">
            {pickupItems.length === 0 && <div className="text-sm text-slate-500">No pickup requests yet.</div>}
            {pickupItems.map((r) => {
              const isCancelled = r.status === "cancelled";
              const canCancel = !isCancelled && r.status !== "denied";
              return (
              <div key={r.id} className={`border rounded-xl p-3 ${isCancelled ? "border-slate-200 opacity-60" : "border-slate-200"}`} data-testid={`my-pickup-${r.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm">
                    <div className="font-medium">
                      {r.shift_label || r.shift_code} <span className="text-slate-500 font-normal">({r.shift_code})</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {mdyDow(r.date)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] uppercase tracking-widest font-semibold rounded-md border px-2 py-0.5 ${
                      r.status === "approved"  ? "bg-sky-100 text-sky-800 border-sky-300" :
                      r.status === "denied"    ? "bg-red-100 text-red-700 border-red-300" :
                      r.status === "cancelled" ? "bg-slate-100 text-slate-600 border-slate-300" :
                                                 "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300"
                    }`}>{r.status}</span>
                    {canCancel && (
                      <button
                        onClick={() => cancelPickup(r)}
                        disabled={cancelling === `pickup:${r.id}`}
                        className="text-[11px] rounded-md border border-red-300 bg-red-50 hover:bg-red-100 text-red-700 px-2 py-0.5 font-semibold disabled:opacity-50"
                        data-testid={`cancel-pickup-${r.id}`}
                      >Cancel</button>
                    )}
                  </div>
                </div>
                {r.manager_note && <div className="mt-2 text-xs italic text-slate-600">Manager note: {r.manager_note}</div>}
              </div>
            );})}
          </div>
        </div>
      </div>
    </div>
  );
}
